import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";

// Ability
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";

// Others
import icons from "enums/icons";
import { urlPaths } from "enums/urlPaths";
import { capitalize, isEmpty } from "lodash";

// MUI
import { Box, Button, Chip, Grid, Typography, Switch, FormControlLabel, Skeleton, useTheme } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

// Components
import {
    DashboardGroup,
    SearchAiPlaceholder,
    LeadershipPlaceholder,
    BriefCard,
    ExpandableCardRow,
    SettingsDropDown,
} from "./components";
import SummaryCard, { SummaryCardRow } from "./components/SummaryCard/SummaryCard";
import EntityList, { RowHead, Row } from "./components/EntityList/EntityList";
import CustomIcon from "components/CustomIcon";
import { HeaderStep } from "components/HeaderStep";
import { ErrorBoundary } from "react-error-boundary";
import GenericError from "components/Errors/GenericError";
import { Header, Body } from "components/Layout/LayoutContent";
import { CompanySummaryHeader } from "components/CompanySummary";
import CompanyInfoCard from "components/CompanyInfoCard";
import NavButton from "components/HeaderNavigation/NavButton";
import Modal from "components/Modal";
import { useSnackBar, SnackBar } from "components/SnackBar";
import MessageUpgrade from "components/Pro/Messages/MessageUpgrade";
import { HeaderInner } from "components/Layout/LayoutContent";
import CRMExport from "components/Team/CRMExport";
import { CreateProjectSectionModal } from "components/CreateProjectSectionModal";

// Adapters
import {
    getCompaniesList,
    getPeopleList,
    getSearchAIList,
    getBriefList,
    getInsightsList,
    SnackBarEntityTypes,
    getProjectCompany,
    convertProjectCompany,
    TConvertedCompaniesList,
    TConvertedPeopleList,
    convertProjectPerson,
    convertCompanyData,
    getProjectBriefsChildren,
    getProjectInsightsChildren,
    getProjectSearchAIsChildren,
    projectChunksToProjectData,
} from "./adapters/formattedData";

// Store
import { toggleLoader } from "store/slice/UI";
import { clearDomain } from "store/slice/Domain";
import { clearChart } from "store/slice/charts/chartsSlice";
import {
    resetBriefData,
    getProject,
    fetchProjectById,
    getProjectCompanyData,
    fetchProjectCompany,
} from "store/slice/Project/projectData/ProjectSlice";
import { clearLongList, clearRefinementData, clearShortList } from "store/slice/refinement";
import { clearBoardStructure, clearMightBeList } from "store/slice/Team";
import { addInsightsData, getInsights } from "store/slice/Project/insights/insightsSlice";
import { addBriefsData, getBriefs } from "store/slice/Project/briefs/briefsSlice";
import { addSearchAisData, getSearchAis } from "store/slice/Project/searchAis/searchAisSlice";
import { resetAdvancedPeers } from "store/slice/advancedPeersSearch";
import { getCompanySets } from "store/slice/Project/companiesSet/companiesSetSlice";
import { getPeopleSets } from "store/slice/Project/peopleSet/peopleSetSlice";

// Hooks
import { useMount } from "ahooks";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useAppziWidget, useAuth, useRedirectOnProjectError, useRequestSet } from "utils/hooks";
import useCreateProjectSectionModal from "components/CreateProjectSectionModal/useCreateProjectSectionModal";

// Types
import {
    BriefChildren,
    InsightData,
    ProjectDataAvatarHistory,
    ProjectDataResponse,
    ProjectSearchAICreationResponse,
} from "services/projects/project.types";
import { ButtonWithCounter } from "components/Buttons";
import { TModalFor } from "components/CreateProjectSectionModal/CreateProjectSectionModal";
import ProText from "components/Pro/ProText";
import { clearICDashboard } from "store/slice/Project/icDashboard/icDashboard.slice";

const showInfoInsightInitial: {
    id: number;
    clickedOn: "Path" | "Brief" | "";
} = {
    id: 0,
    clickedOn: "",
};

const entityInitialState = { searchAiId: 0, type: undefined, data: [] };

const ProjectDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const theme = useTheme();

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError();

    useMount(() => {
        dispatch(clearDomain());
        dispatch(resetAdvancedPeers());
        dispatch(resetBriefData());
        dispatch(clearBoardStructure());
        dispatch(clearLongList());
        dispatch(clearShortList());
        dispatch(clearRefinementData());
        dispatch(clearMightBeList());
        dispatch(clearICDashboard());
    });

    const ability = useAbility(AbilityContext);

    const canCreateInsights = ability.can("see", "SAAS_LD_PROJECT_INSIGHT_CREATE");
    const canCreateBriefs = ability.can("see", "SAAS_LD_PROJECT_BRIEF_CREATE") || !ability.can("see", "LD_FREE");
    const canCreateSearchAI = ability.can("see", "SAAS_LD_PROJECT_SEARCH_AI_CREATE") || !ability.can("see", "LD_FREE");

    const { projectId } = useParams();
    const { loggedInUserData } = useAuth();
    const { handleOpenWidget } = useAppziWidget();
    const { requestSets, getCompanySetPath, getPersonSetPath } = useRequestSet();

    const { data: projectData } = useAppSelector(getProject);
    const { data: projectCompanyData } = useAppSelector(getProjectCompanyData);
    const companyDataForSummaryCard = convertCompanyData(projectCompanyData);
    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();

    const projectChunks = {
        data: useAppSelector(getProject).data,
        insights: useAppSelector(getInsights).data,
        briefs: useAppSelector(getBriefs).data,
        searchAIs: useAppSelector(getSearchAis).data,
        companySets: useAppSelector(getCompanySets).data,
        personSets: useAppSelector(getPeopleSets).data,
    };

    const [isNameInvalid, setIsNameInvalid] = useState(true);
    const [currentParentId, setCurrentParentId] = useState(0);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [insightsFilterValue, setInsightsFilterValue] = useState("");
    const [briefsFilterValue, setBriefsFilterValue] = useState("");
    const [searchAIFilterValue, setSearchAIFilterValue] = useState("");

    const [showInfoForInsight, setShowInfoForInsight] = useState<typeof showInfoInsightInitial>(showInfoInsightInitial);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [companyList, setCompanyList] = useState<ReturnType<typeof getCompaniesList>>(
        getCompaniesList(projectData) || []
    );
    const [peopleList, setPeopleList] = useState<ReturnType<typeof getPeopleList>>(getPeopleList(projectData) || []);
    const [insightsList, setInsightsList] = useState<ReturnType<typeof getInsightsList>>(
        getInsightsList(projectData) || []
    );
    const [briefList, setBriefList] = useState<ReturnType<typeof getBriefList>>(getBriefList(projectData) || []);
    const [searchAIList, setSearchAIList] = useState<ReturnType<typeof getSearchAIList>>(
        getSearchAIList(projectData) || []
    );
    const [selectedEntity, setSelectedEntity] = useState<{
        searchAiId: number;
        type: keyof typeof SnackBarEntityTypes | undefined;
        data: (TConvertedPeopleList | TConvertedCompaniesList)[];
    }>(entityInitialState);

    const [openCRMExport, setOpenCRMExport] = useState(false);
    const [selectedSearchAiId, setSelectedSearchAiId] = useState<number | null>(null);

    const wrapperRef = useRef<HTMLElement | null | undefined>(null);

    const companyId = projectCompanyData?.id;
    const headerSteps = useMemo(
        () =>
            [
                { label: "Create Leadership Dashboard", shouldRender: true, shouldBeEnabled: true },
                { label: "Create Your brief", shouldRender: true, shouldBeEnabled: canCreateBriefs },
                {
                    label: "Start Your Search",
                    shouldRender: canCreateSearchAI,
                    shouldBeEnabled: true,
                },
            ].filter((e) => e.shouldRender),
        [canCreateSearchAI, canCreateBriefs]
    );

    const handleCallBackAfterSaving = async (
        createdSectionData: InsightData | BriefChildren | ProjectSearchAICreationResponse
    ) => {
        if (showModalFor === "LD Dashboard") {
            const insightId = createdSectionData?.data.id;

            if (insightId) {
                dispatch(clearDomain());
                dispatch(toggleLoader(false));
                navigate(`${urlPaths.Domain}/${companyId}?projectId=${projectId}&insightId=${insightId}`, {
                    state: {
                        from: `${urlPaths.ProjectDashboard}/${projectId}`,
                    },
                });
            }
        }

        if (showModalFor === "Brief") {
            const briefId = createdSectionData?.data.id;

            if (briefId) {
                dispatch(clearChart());
                dispatch(resetBriefData());
                dispatch(toggleLoader(false));
                navigate(
                    `${urlPaths.Brief}/${companyId}?projectId=${projectId}&insightId=${currentParentId}&briefId=${briefId}`,
                    {
                        state: {
                            from: `${urlPaths.ProjectDashboard}/${projectId}`,
                        },
                    }
                );
            }
        }

        if (showModalFor === "SearchAi") {
            const searchAiId = createdSectionData?.data.id;
            const insightId = briefList.find((brief) => brief.id === currentParentId)?.meta.relatedInsight?.id;

            if (searchAiId && insightId) {
                dispatch(toggleLoader(false));
                navigate(
                    `${urlPaths.Talent}/${companyId}?projectId=${projectId}&insightId=${insightId}&searchAiId=${searchAiId}`,
                    {
                        state: {
                            from: `${urlPaths.ProjectDashboard}/${projectId}`,
                        },
                    }
                );
            }
        }
    };

    const {
        showModalFor,
        newSectioName,
        handleShowCreateProjectSectionModal,
        handleCreateProjectSection,
        handleUpdateSectionName,
        handleCloseProjectSectionCreateModal,
    } = useCreateProjectSectionModal(projectId, currentParentId, handleCallBackAfterSaving);

    const getShowInfoForInsightData = (insightId: number) => insightsList?.find((insight) => insight.id === insightId);

    const getInsightMissingPortion = (insightId: number) => {
        const insightData = getShowInfoForInsightData(insightId);
        const urlSearchParams = `${insightData?.meta.companyId}?projectId=${projectId}&insightId=${insightData?.id}`;

        if (!insightData?.meta?.peers) {
            return {
                url: `${urlPaths.Domain}/${urlSearchParams}`,
                label: "Complete Set Domain",
            };
        }

        if (!insightData?.meta?.slt) {
            return {
                url: `${urlPaths.Team}/${urlSearchParams}`,
                label: "Complete Set Team",
            };
        }

        if (!insightData?.meta?.vcp) {
            return {
                url: `${urlPaths.Vcp}/${urlSearchParams}`,
                label: "Complete VCP",
            };
        }

        return {
            url: `${urlPaths.ProjectDashboard}/${projectId}`,
            label: "Go to Dashboard",
        };
    };

    const handleClickSearchAICount = async (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
        searchAI: any,
        type: keyof typeof SnackBarEntityTypes
    ) => {
        handleOpenSnackBar(event);
        setSelectedEntity((prevValue) => ({
            ...prevValue,
            searchAiId: searchAI.id,
            type,
        }));

        if (type === "company") {
            const path = getCompanySetPath(projectData.data.id, searchAI.meta.relatedCompanyListId);
            const response = await requestSets(projectData.data.id, [path], type);
            const list: TConvertedCompaniesList[] = response.map((c: any) => convertProjectCompany(c.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }

        if (type === "people") {
            const path = getPersonSetPath(projectData.data.id, searchAI.meta.relatedLongListId);
            const response = await requestSets(projectData.data.id, [path], type);
            const list: TConvertedPeopleList[] = response.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }

        if (type === "shortlist") {
            const path = getPersonSetPath(projectData.data.id, searchAI.meta.relatedShortListId);
            const response = await requestSets(projectData.data.id, [path], "people");
            const list: TConvertedPeopleList[] = response.map((p: any) => convertProjectPerson(p.data));
            setSelectedEntity((prevVal) => ({ ...prevVal, data: list }));
        }
    };

    const insightsFilteredList = insightsList.filter((item) =>
        item.name.toLowerCase().includes(insightsFilterValue.toLowerCase())
    );
    const briefsFilteredList = briefList.filter((item) =>
        item.name.toLowerCase().includes(briefsFilterValue.toLowerCase())
    );
    const searchAIFilteredList = searchAIList.filter((item) =>
        item.name.toLowerCase().includes(searchAIFilterValue.toLowerCase())
    );

    const handleOpenModal = async () => {
        if (isEmpty(projectCompanyData)) return;
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleCreate = async (type: TModalFor) => {
        handleShowCreateProjectSectionModal(type);
    };

    const handleHighlightRelatedWork = (id: number, type: "searchAi" | "brief" | "insight" | "company" | "people") => {
        if (type === "searchAi") {
            const selectedSearchAI = searchAIList.find((s) => s.id === id);
            const relatedBriefId = selectedSearchAI?.meta?.relatedBrief?.id;

            // Insight
            const selectedBrief = briefList.find((b) => b.id === relatedBriefId);
            const relatedInsightId = selectedBrief?.meta.relatedInsight?.id;

            // Brief
            const selectedBriefsIds = briefList
                .filter((s) => s.meta.relatedInsight?.id === relatedInsightId)
                .map((s) => s.id);

            // SearchAIs
            const selectedSearchAis = searchAIList.filter(
                (s) => s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id)
            );

            // Company and People
            const selectedInsight = insightsList.find((i) => i.id === relatedInsightId);

            const relatedCompanyIds = [
                selectedInsight?.meta.peers,
                ...selectedSearchAis.map((s) => s?.meta.relatedCompanyListId),
            ];
            const relatedPeopleIds = [
                selectedInsight?.meta.slt,
                ...selectedSearchAis.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
            ];

            const shouldToggle = !selectedSearchAI?.shouldHighlight;

            setSearchAIList((current) =>
                current.map((s) => ({
                    ...s,
                    shouldHide:
                        shouldToggle &&
                        !Boolean(s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id)),
                    shouldHighlight: Boolean(
                        s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id) && shouldToggle
                    ),
                }))
            );

            setBriefList((current) =>
                current.map((b) => ({
                    ...b,
                    shouldHide: shouldToggle && !selectedBriefsIds.includes(b.id),
                    shouldHighlight: selectedBriefsIds.includes(b.id) && shouldToggle,
                }))
            );

            setInsightsList((current) =>
                current.map((i) => ({
                    ...i,
                    shouldHide: shouldToggle && i.id !== relatedInsightId,
                    shouldHighlight: i.id === relatedInsightId && shouldToggle,
                }))
            );

            setCompanyList((current) =>
                current?.map((c) => ({
                    ...c,
                    shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                    shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                }))
            );

            setPeopleList((current) =>
                current.map((p) => ({
                    ...p,
                    shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                    shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                }))
            );
        }

        if (type === "brief") {
            // Insight
            const selectedBrief = briefList.find((b) => b.id === id);
            const relatedInsightId = selectedBrief?.meta.relatedInsight?.id;
            const selectedInsight = insightsList.find((i) => i.id === relatedInsightId);

            // Brief
            const relatedBriefIds = briefList
                .filter((b) => b.meta?.relatedInsight?.id === relatedInsightId)
                .map((b) => b.id);

            // SearchAi
            const selectedSearchAi = searchAIList.filter(
                (s) => s.meta?.relatedBrief?.id && relatedBriefIds.includes(s.meta.relatedBrief.id)
            );
            const selectedSearchAiIds = selectedSearchAi.map((s) => s.id);

            // Company and People
            const relatedCompanyIds = [
                selectedInsight?.meta.peers,
                ...selectedSearchAi.map((s) => s?.meta.relatedCompanyListId),
            ];
            const relatedPeopleIds = [
                selectedInsight?.meta.slt,
                ...selectedSearchAi.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
            ];

            const shouldToggle = !selectedBrief?.shouldHighlight;

            setSearchAIList((current) =>
                current.map((s) => ({
                    ...s,
                    shouldHide: shouldToggle && !selectedSearchAiIds.includes(s.id),
                    shouldHighlight: selectedSearchAiIds.includes(s.id) && shouldToggle,
                }))
            );

            setBriefList((current) =>
                current.map((b) => ({
                    ...b,
                    shouldHide: shouldToggle && !relatedBriefIds.includes(b.id),
                    shouldHighlight: relatedBriefIds.includes(b.id) && shouldToggle,
                }))
            );

            setInsightsList((current) =>
                current.map((i) => ({
                    ...i,
                    shouldHide: shouldToggle && i.id !== relatedInsightId,
                    shouldHighlight: i.id === relatedInsightId && shouldToggle,
                }))
            );

            setCompanyList((current) =>
                current?.map((c) => ({
                    ...c,
                    shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                    shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                }))
            );

            setPeopleList((current) =>
                current.map((p) => ({
                    ...p,
                    shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                    shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                }))
            );
        }

        if (type === "insight") {
            // Insight
            const selectedInsight = insightsList.find((i) => i.id === id);

            // Brief
            const selectedBriefsIds = briefList.filter((s) => s.meta.relatedInsight?.id === id).map((s) => s.id);

            // SearchAi
            const selectedSearchAi = searchAIList.filter(
                (s) => s.meta?.relatedBrief && selectedBriefsIds.includes(s.meta.relatedBrief.id)
            );
            const selectedSearchAiIds = selectedSearchAi.map((s) => s.id);

            // Company and People
            const relatedCompanyIds = [
                selectedInsight?.meta.peers,
                ...selectedSearchAi.map((s) => s?.meta.relatedCompanyListId),
            ];
            const relatedPeopleIds = [
                selectedInsight?.meta.slt,
                ...selectedSearchAi.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
            ];

            const shouldToggle = !selectedInsight?.shouldHighlight;

            setSearchAIList((current) =>
                current.map((s) => ({
                    ...s,
                    shouldHide: shouldToggle && !selectedSearchAiIds.includes(s.id),
                    shouldHighlight: selectedSearchAiIds.includes(s.id) && shouldToggle,
                }))
            );

            setBriefList((current) =>
                current.map((b) => ({
                    ...b,
                    shouldHide: shouldToggle && !selectedBriefsIds.includes(b.id),
                    shouldHighlight: selectedBriefsIds.includes(b.id) && shouldToggle,
                }))
            );

            setInsightsList((current) =>
                current.map((i) => ({
                    ...i,
                    shouldHide: shouldToggle && i.id !== id,
                    shouldHighlight: i.id === id && shouldToggle,
                }))
            );

            setCompanyList((current) =>
                current?.map((c) => ({
                    ...c,
                    shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                    shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                }))
            );

            setPeopleList((current) =>
                current.map((p) => ({
                    ...p,
                    shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                    shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                }))
            );
        }

        if (type === "company" || type === "people") {
            const selectedList =
                type === "company" ? companyList?.find((i) => i.id === id) : peopleList?.find((i) => i.id === id);

            // SearchAI
            const searchAI = searchAIList.find(
                (s) =>
                    selectedList &&
                    (s.meta.relatedCompanyListId === selectedList?.id ||
                        s.meta.relatedLongListId === selectedList?.id ||
                        s.meta.relatedShortListId === selectedList?.id)
            );

            // The if part should be exaclty the same as if the id belongs to a searchAi
            if (searchAI) {
                const selectedSearchAI = searchAIList.find((s) => s.id === searchAI?.id);
                const relatedBriefId = selectedSearchAI?.meta?.relatedBrief?.id;

                // Insight
                const selectedBrief = briefList.find((b) => b.id === relatedBriefId);
                const relatedInsightId = selectedBrief?.meta.relatedInsight?.id;

                // Brief
                const selectedBriefsIds = briefList
                    .filter((s) => s.meta.relatedInsight?.id === relatedInsightId)
                    .map((s) => s.id);

                // SearchAIs
                const selectedSearchAis = searchAIList.filter(
                    (s) => s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id)
                );

                // Company and People
                const selectedInsight = insightsList.find((i) => i.id === relatedInsightId);

                const relatedCompanyIds = [
                    selectedInsight?.meta.peers,
                    ...selectedSearchAis.map((s) => s?.meta.relatedCompanyListId),
                ];
                const relatedPeopleIds = [
                    selectedInsight?.meta.slt,
                    ...selectedSearchAis.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
                ];

                const shouldToggle = !selectedSearchAI?.shouldHighlight;

                setSearchAIList((current) =>
                    current.map((s) => ({
                        ...s,
                        shouldHide:
                            shouldToggle &&
                            !Boolean(s.meta?.relatedBrief?.id && selectedBriefsIds.includes(s.meta.relatedBrief.id)),
                        shouldHighlight: Boolean(
                            s.meta?.relatedBrief?.id &&
                                selectedBriefsIds.includes(s.meta.relatedBrief.id) &&
                                shouldToggle
                        ),
                    }))
                );

                setBriefList((current) =>
                    current.map((b) => ({
                        ...b,
                        shouldHide: shouldToggle && !selectedBriefsIds.includes(b.id),
                        shouldHighlight: selectedBriefsIds.includes(b.id) && shouldToggle,
                    }))
                );

                setInsightsList((current) =>
                    current.map((i) => ({
                        ...i,
                        shouldHide: shouldToggle && i.id !== relatedInsightId,
                        shouldHighlight: i.id === relatedInsightId && shouldToggle,
                    }))
                );

                setCompanyList((current) =>
                    current?.map((c) => ({
                        ...c,
                        shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                        shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                    }))
                );

                setPeopleList((current) =>
                    current.map((p) => ({
                        ...p,
                        shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                        shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                    }))
                );
            } else {
                // Insight
                const selectedInsights = insightsList.filter(
                    (i) => selectedList && i.meta[type === "company" ? "peers" : "slt"] === selectedList.id
                );
                const selectedInsightsIds = insightsList
                    .filter((i) => selectedList && i.meta[type === "company" ? "peers" : "slt"] === selectedList.id)
                    .map((i) => i.id);

                // Briefs
                const selectedBriefsIds = briefList
                    .filter((b) => b.meta.relatedInsight && selectedInsightsIds.includes(b.meta.relatedInsight.id))
                    .map((b) => b.id);

                // Search Ais
                const selectedSearchAis = searchAIList.filter(
                    (s) => s.meta.relatedBrief && selectedBriefsIds.includes(s.meta.relatedBrief.id)
                );
                const selectedSearchAiIds = selectedSearchAis.map((s) => s.id);

                // Company and People
                const relatedCompanyIds = [
                    ...selectedInsights.map((i) => i.meta.peers),
                    ...selectedSearchAis.map((s) => s?.meta.relatedCompanyListId),
                ];
                const relatedPeopleIds = [
                    ...selectedInsights.map((i) => i.meta.slt),
                    ...selectedSearchAis.flatMap((s) => [s?.meta.relatedLongListId, s?.meta.relatedShortListId]),
                ];

                const shouldToggle = !selectedList?.shouldHighlight;

                setCompanyList((current) =>
                    current?.map((c) => ({
                        ...c,
                        shouldHide: shouldToggle && !relatedCompanyIds.includes(c.id),
                        shouldHighlight: shouldToggle && relatedCompanyIds.includes(c.id),
                    }))
                );

                setPeopleList((current) =>
                    current.map((p) => ({
                        ...p,
                        shouldHide: shouldToggle && !relatedPeopleIds.includes(p.id),
                        shouldHighlight: shouldToggle && relatedPeopleIds.includes(p.id),
                    }))
                );

                setInsightsList((current) =>
                    current.map((s) => ({
                        ...s,
                        shouldHide: shouldToggle && !selectedInsightsIds.includes(s.id),
                        shouldHighlight: selectedInsightsIds.includes(s.id) && shouldToggle,
                    }))
                );

                setBriefList((current) =>
                    current.map((s) => ({
                        ...s,
                        shouldHide: shouldToggle && !selectedBriefsIds.includes(s.id),
                        shouldHighlight: selectedBriefsIds.includes(s.id) && shouldToggle,
                    }))
                );

                setSearchAIList((current) =>
                    current.map((s) => ({
                        ...s,
                        shouldHide: shouldToggle && !selectedSearchAiIds.includes(s.id),
                        shouldHighlight: selectedSearchAiIds.includes(s.id) && shouldToggle,
                    }))
                );
            }
        }
    };

    const handleHighlightMyWork = (isChecked: boolean) => {
        const getMyLogsCount = (activityLog: ProjectDataAvatarHistory[] | undefined) =>
            Boolean(activityLog?.filter((log) => log.id === loggedInUserData.userId).length);

        setCompanyList((current) =>
            current?.map((c) => ({
                ...c,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(c.activityLog),
            }))
        );

        setPeopleList((current) =>
            current.map((p) => ({
                ...p,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(p.activityLog),
            }))
        );

        setInsightsList((current) =>
            current.map((i) => ({
                ...i,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(i.activityLog),
            }))
        );

        setBriefList((current) =>
            current.map((b) => ({
                ...b,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(b.activityLog),
            }))
        );

        setSearchAIList((current) =>
            current.map((s) => ({
                ...s,
                shouldHighlight: false,
                shouldHide: isChecked && !getMyLogsCount(s.activityLog),
            }))
        );
    };

    // Check creation name while typing
    useEffect(() => {
        const handleIsNameInvalid = (list: any) => {
            return Boolean(list.find((element: any) => element?.name.toLowerCase() === newSectioName.toLowerCase()));
        };

        if (!newSectioName || !showModalFor) return;

        if (showModalFor?.includes("LD Dashboard")) {
            setIsNameInvalid(handleIsNameInvalid(insightsList));
        }

        if (showModalFor?.includes("Brief")) {
            setIsNameInvalid(handleIsNameInvalid(briefList));
        }

        if (showModalFor?.includes("SearchAi")) {
            setIsNameInvalid(handleIsNameInvalid(searchAIList));
        }
    }, [briefList, insightsList, searchAIList, newSectioName, showModalFor]);

    // Initial Fetch
    useEffect(() => {
        const initialFetch = async () => {
            let projectResponse = {} as ProjectDataResponse;
            setIsLoadingData(true);

            if (isEmpty(projectData) && projectId) {
                try {
                    const data = await dispatch(fetchProjectById({ projectId: Number(projectId) }));
                    projectResponse = unwrapResult(data);
                } catch (error) {
                    console.error(error);
                    setIsLoadingData(false);
                }
            }

            const companyId = getProjectCompany(projectResponse)?.data.id;

            if (isEmpty(projectCompanyData) && companyId) {
                try {
                    await dispatch(fetchProjectCompany(companyId));
                } catch (error) {
                    console.error(error);
                    setIsLoadingData(false);
                }
            }

            const data = !isEmpty(projectResponse)
                ? projectResponse
                : projectChunksToProjectData(projectData, projectChunks);
            setData(data);
            setIsLoadingData(false);
        };

        const setData = (data: ProjectDataResponse) => {
            setCompanyList(getCompaniesList(data) || []);
            setPeopleList(getPeopleList(data) || []);
            setInsightsList(getInsightsList(data) || []);
            setBriefList(getBriefList(data) || []);
            setSearchAIList(getSearchAIList(data) || []);

            dispatch(addInsightsData(getProjectInsightsChildren(data)));
            dispatch(addBriefsData(getProjectBriefsChildren(data)));
            dispatch(addSearchAisData(getProjectSearchAIsChildren(data)));
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCRMExport = (id: number) => {
        setSelectedSearchAiId(id);
        setOpenCRMExport(true);
    };

    const handleViewLeadershipDashboard = (insightId: number) => {
        const insightData = getShowInfoForInsightData(insightId);
        dispatch(clearChart());

        navigate(
            `${urlPaths.LeadershipOverview}/${insightData?.meta.companyId}?projectId=${projectId}&insightId=${insightId}`
        );
    };

    return (
        <ErrorBoundary FallbackComponent={GenericError}>
            <Header backgroundColor="primary" sx={{ px: 3 }}>
                <HeaderInner>
                    {isLoadingData ? (
                        <Skeleton variant="rounded" width={250} height={42} />
                    ) : (
                        <CompanySummaryHeader title={projectCompanyData?.name} />
                    )}
                    {!isLoadingData && (
                        <Box display="flex" alignItems="center">
                            {headerSteps.map((el, i) => {
                                return (
                                    <HeaderStep
                                        key={i}
                                        disabled={!el.shouldBeEnabled}
                                        innerLabel={
                                            el.shouldBeEnabled ? (
                                                `${i + 1}`
                                            ) : (
                                                <CustomIcon icon={icons.lock} sx={{ fontSize: 18 }} />
                                            )
                                        }
                                        label={el.label}
                                        shouldShowProText={!el.shouldBeEnabled}
                                        sx={{ marginRight: i < headerSteps.length - 1 ? 3 : 0 }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                </HeaderInner>
            </Header>
            <Body>
                {/* Summary / Companies and People lists */}
                <Box
                    sx={{
                        display: "flex",
                        gap: 1.5,
                        py: 1.5,
                        px: 3,
                        boxShadow:
                            "0 2px 2px 0 rgba(0, 0, 0, 0.03), 0 5px 5px 0 rgba(0, 0, 0, 0.05), 0 1px 4px 0 rgba(0, 0, 0, 0.03);",
                    }}
                >
                    <Box sx={{ width: "38%" }}>
                        {isLoadingData || companyDataForSummaryCard === undefined ? (
                            <Skeleton variant="rounded" width="100%" height={54} />
                        ) : (
                            <SummaryCard>
                                <SummaryCardRow config={companyDataForSummaryCard.products} />
                                <SummaryCardRow
                                    config={companyDataForSummaryCard.sectors}
                                    onOpenModal={handleOpenModal}
                                />
                            </SummaryCard>
                        )}
                    </Box>
                    <Box sx={{ width: "31%" }}>
                        {isLoadingData ? (
                            <Skeleton variant="rounded" width="100%" height={54} />
                        ) : (
                            <EntityList
                                title="People Lists"
                                dataType="people"
                                data={peopleList}
                                onClickRelatedButton={handleHighlightRelatedWork}
                            />
                        )}
                    </Box>
                    <Box sx={{ width: "31%" }}>
                        {isLoadingData ? (
                            <Skeleton variant="rounded" width="100%" height={54} />
                        ) : (
                            <EntityList
                                title="Company Lists"
                                dataType="company"
                                data={companyList}
                                onClickRelatedButton={handleHighlightRelatedWork}
                            />
                        )}
                    </Box>
                </Box>

                {/* My Work */}
                <Grid container spacing={1} sx={{ mt: 1.2, mb: 2, px: 2, position: "relative", zIndex: 2 }}>
                    <Grid item lg={12} display="flex" alignItems="center" justifyContent="flex-end">
                        {isLoadingData ? (
                            <Skeleton variant="rounded" width={124} height={32} />
                        ) : (
                            <FormControlLabel
                                sx={{ flexDirection: "row-reverse" }}
                                label={<Typography sx={{ fontSize: 16 }}>My Work</Typography>}
                                control={
                                    <Switch
                                        size="small"
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                            handleHighlightMyWork(event.target.checked)
                                        }
                                    />
                                }
                            />
                        )}
                    </Grid>
                </Grid>

                <Box sx={{ position: "relative" }}>
                    {/* LD Dashboards */}
                    <DashboardGroup
                        number={1}
                        data={insightsFilteredList}
                        filterValue={insightsFilterValue}
                        title="Leadership Dashboard"
                        description="Create a Dashboard by setting the team, domain and VCP."
                        isLoading={isLoadingData}
                        handleCreate={handleCreate}
                        handleFilter={setInsightsFilterValue}
                        disabled={isLoadingData}
                        sx={{ px: 2 }}
                    >
                        <>
                            {/* Loader */}
                            {isLoadingData && (
                                <>
                                    <Skeleton variant="rounded" width="100%" height={64} sx={{ mt: 1, mb: 1 }} />
                                    <Skeleton variant="rounded" width="100%" height={64} />
                                </>
                            )}

                            {/* No data placeholder */}
                            {!isLoadingData && !insightsList.length && canCreateInsights && <LeadershipPlaceholder />}

                            {/* Has Data */}
                            <Box
                                sx={{
                                    pr: 1,
                                    maxHeight: 230,
                                    overflowY: "scroll",
                                    alignItems: "center",
                                }}
                            >
                                {!isLoadingData &&
                                    insightsFilteredList.length > 0 &&
                                    insightsFilteredList.map((insight, i) => {
                                        const canGenerateProjectDashboard =
                                            Boolean(insight.meta.peers) &&
                                            Boolean(insight.meta.slt) &&
                                            insight.meta.sltCount > 0;

                                        return (
                                            <Box
                                                key={insight.id}
                                                sx={{
                                                    display: insight.shouldHide ? "none" : "flex",
                                                    alignItems: "center",
                                                    borderBottom:
                                                        i !== insightsFilteredList.length - 1
                                                            ? `1px solid #EAEAEA`
                                                            : "",
                                                }}
                                            >
                                                <ExpandableCardRow
                                                    type="insight"
                                                    onClickRelatedButton={handleHighlightRelatedWork}
                                                    isDisabled={insight.isDisabled}
                                                    data={insight}
                                                    setShowInfoForInsight={setShowInfoForInsight}
                                                >
                                                    <>
                                                        <SettingsDropDown projectId={projectId} insight={insight} />
                                                        <Button
                                                            sx={{
                                                                p: 0,
                                                                ml: 3,
                                                                opacity: !canGenerateProjectDashboard ? 0.4 : 1,
                                                            }}
                                                            onClick={() => handleViewLeadershipDashboard(insight.id)}
                                                            disabled={!canGenerateProjectDashboard}
                                                        >
                                                            <CustomIcon
                                                                icon={icons.triangleAndLines}
                                                                sx={{ fontSize: 16, mr: 1 }}
                                                            />
                                                            <Typography
                                                                sx={{ fontSize: 13, textDecoration: "underline" }}
                                                            >
                                                                View Leadership Dashboard
                                                            </Typography>
                                                        </Button>
                                                    </>
                                                </ExpandableCardRow>

                                                {!insight.isDisabled ? (
                                                    <Box sx={{ ml: 2 }}>
                                                        <Button
                                                            sx={{
                                                                background: theme.palette.others.green100,
                                                                minWidth: "max-content",
                                                                fontSize: 13,
                                                                height: 32,
                                                                padding: "6px 16px",
                                                                "&.Mui-disabled": {
                                                                    opacity: canGenerateProjectDashboard ? 1 : 0.5,
                                                                },
                                                                "& .MuiButton-startIcon > *:nth-of-type(1)": {
                                                                    fontSize: "16px !important",
                                                                    color: theme.palette.others.green900,
                                                                    transition: "all 0.3s ease",
                                                                },
                                                                "&:hover": {
                                                                    background: theme.palette.others.green900,
                                                                    color: theme.palette.others.green100,
                                                                    "& .MuiButton-startIcon > *:nth-of-type(1)": {
                                                                        color: theme.palette.common.white,
                                                                    },
                                                                },
                                                            }}
                                                            disabled={!canGenerateProjectDashboard}
                                                            onClick={() => {
                                                                if (!canCreateBriefs) {
                                                                    handleOpenWidget();
                                                                    return;
                                                                }

                                                                if (!canGenerateProjectDashboard) {
                                                                    setShowInfoForInsight({
                                                                        id: insight.id,
                                                                        clickedOn: "Brief",
                                                                    });
                                                                } else {
                                                                    setCurrentParentId(insight.id);
                                                                    handleCreate("Brief");
                                                                }
                                                            }}
                                                            startIcon={<CustomIcon icon={icons.documentLightBulb} />}
                                                        >
                                                            Create Brief
                                                            {!canCreateBriefs && <ProText sx={{ ml: 1 }} />}
                                                        </Button>
                                                    </Box>
                                                ) : (
                                                    <></>
                                                )}
                                            </Box>
                                        );
                                    })}
                            </Box>

                            {/* No Results from API and no permissions */}
                            {!isLoadingData &&
                                !canCreateInsights &&
                                !insightsFilteredList.length &&
                                !insightsFilterValue && (
                                    <Box
                                        sx={{
                                            backgroundColor: theme.palette.background.paleBlue,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: 225,
                                            borderRadius: 1.5,
                                        }}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <CustomIcon icon={icons.sparkles} />
                                            <Typography ml={0.5} variant="subtitle2">
                                                Create your first LD Dashboard to start your search.
                                            </Typography>
                                        </Box>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            sx={{ mt: 1, height: 36 }}
                                            onClick={() => handleCreate("LD Dashboard")}
                                        >
                                            <CustomIcon icon={icons.plus} sx={{ fontSize: "12px !important", mr: 1 }} />
                                            <FormattedMessage id="project.dashboard.new.dashboard" />
                                        </Button>
                                    </Box>
                                )}

                            {/* No Results from filter */}
                            {!isLoadingData && !insightsFilteredList.length && insightsFilterValue && (
                                <Box sx={{ display: "flex", height: 170 }}>
                                    <Typography variant="subtitle2" sx={{ margin: "auto" }}>
                                        <FormattedMessage id="dashboard.filters.noResults" />
                                    </Typography>
                                </Box>
                            )}
                        </>
                    </DashboardGroup>

                    {/* Briefs */}
                    <DashboardGroup
                        number={2}
                        data={briefsFilteredList}
                        filterValue={briefsFilterValue}
                        title="Briefs"
                        description="The brief, based on Leadership Dashboard, will clarify your search criteria and outline the profile you aim to identify."
                        isLoading={isLoadingData}
                        handleFilter={setBriefsFilterValue}
                        showVerticalLine={canCreateSearchAI}
                        hasPermissions={canCreateBriefs}
                        disabled={!canCreateBriefs || isLoadingData}
                        sx={{ px: 2 }}
                    >
                        <>
                            {/* Loader */}
                            {isLoadingData && (
                                <>
                                    <Skeleton variant="rounded" width="100%" height={64} sx={{ mb: 1 }} />
                                    <Skeleton variant="rounded" width="100%" height={64} />
                                </>
                            )}

                            {/* No data placeholder */}
                            {Boolean(
                                canCreateBriefs && !isLoadingData && !insightsList.length && !briefList.length
                            ) && (
                                <Box
                                    sx={{
                                        p: 4,
                                        backgroundColor: "background.grey",
                                        borderRadius: 0.8,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <CustomIcon icon={icons.lock} sx={{ color: "#454852", fontSize: 20, mr: 1 }} />
                                    <Typography sx={{ color: "#454852" }}>
                                        Complete your insights above to get started
                                    </Typography>
                                </Box>
                            )}

                            {/* No data, but briefs placeholder */}
                            {canCreateBriefs &&
                                !isLoadingData &&
                                Boolean(insightsList.length) &&
                                !Boolean(briefList.length) && (
                                    <Box
                                        sx={{
                                            p: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: theme.spacing(4),
                                            borderRadius: 1.5,
                                            backgroundColor: theme.palette.background.grey,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <CustomIcon
                                                icon={icons.sparkles}
                                                sx={{ color: "#454852", fontSize: 24, mr: 1 }}
                                            />
                                            <CustomIcon
                                                icon={icons.thinArrowRight}
                                                sx={{ color: "#454852", fontSize: 26, mr: 1 }}
                                            />
                                            <CustomIcon
                                                icon={icons.documentLightBulb}
                                                sx={{ color: "#454852", fontSize: 20, mr: 1 }}
                                            />
                                        </Box>
                                        <Typography sx={{ color: "#454852" }}>
                                            <FormattedMessage id="dashboard.briefs.getStarted" />
                                        </Typography>
                                    </Box>
                                )}

                            {/* Has Data */}
                            <Box
                                sx={{
                                    pr: 1,
                                    maxHeight: 260,
                                    overflowY: "scroll",
                                    alignItems: "center",
                                }}
                            >
                                {canCreateBriefs && !isLoadingData && briefsFilteredList.length > 0 && (
                                    <Grid container spacing={2}>
                                        {briefsFilteredList
                                            .filter((brief) => brief.meta?.relatedInsight?.id)
                                            .map((brief) => (
                                                <Grid
                                                    item
                                                    lg={6}
                                                    key={brief.id}
                                                    sx={{
                                                        display: brief.shouldHide ? "none" : "block",
                                                    }}
                                                >
                                                    <BriefCard
                                                        data={brief}
                                                        companyId={companyId}
                                                        onClickCreateSearchAi={() => {
                                                            if (brief.id) {
                                                                setCurrentParentId(brief.id);
                                                                handleCreate("SearchAi");
                                                            }
                                                        }}
                                                        onClickRelatedButton={handleHighlightRelatedWork}
                                                    />
                                                </Grid>
                                            ))}
                                    </Grid>
                                )}
                            </Box>

                            {/* Pro Feature*/}
                            {!isLoadingData && !canCreateBriefs && (
                                <Box
                                    sx={{
                                        p: theme.spacing(2.5),
                                        backgroundColor: theme.palette.background.grey,
                                        borderRadius: 1.5,
                                    }}
                                >
                                    <MessageUpgrade
                                        width={455}
                                        variant="type1"
                                        icon={icons.proStar}
                                        title={"pro.message.heading"}
                                    >
                                        <Typography sx={{ fontSize: 16, margin: theme.spacing(1, 0) }}>
                                            Using insights of the leadership team created, our briefs will help define
                                            your search criteria outlining a detailed leadership profile and scope of
                                            the search.
                                        </Typography>
                                    </MessageUpgrade>
                                </Box>
                            )}

                            {/* No Results from filter */}
                            {!isLoadingData && !briefsFilteredList.length && briefsFilterValue && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        height: 170,
                                        backgroundColor: "background.grey",
                                        margin: "auto",
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ margin: "auto" }}>
                                        <FormattedMessage id="dashboard.filters.noResults" />
                                    </Typography>
                                </Box>
                            )}
                        </>
                    </DashboardGroup>

                    {/* Search AIs */}
                    {canCreateSearchAI && (
                        <DashboardGroup
                            number={3}
                            data={searchAIFilteredList}
                            filterValue={searchAIFilterValue}
                            title="SearchAI"
                            showVerticalLine={false}
                            description="Search AI offers tailored search functions based on your brief. You can explore, modify, and add criteria to improve your search experience."
                            isLoading={isLoadingData}
                            hasPermissions={canCreateSearchAI}
                            disabled={!canCreateSearchAI || isLoadingData}
                            handleFilter={setSearchAIFilterValue}
                            sx={{ px: 2 }}
                        >
                            <>
                                {/* Loader */}
                                {isLoadingData && (
                                    <>
                                        <Skeleton variant="rounded" width="100%" height={64} sx={{ mb: 1 }} />
                                        <Skeleton variant="rounded" width="100%" height={64} />
                                    </>
                                )}

                                {/* No data placeholder */}
                                {Boolean(
                                    canCreateSearchAI && !isLoadingData && !briefList.length && !searchAIList.length
                                ) && <SearchAiPlaceholder hasBriefData={Boolean(!briefList.length)} />}

                                {/* No data, but briefs placeholder */}
                                {Boolean(
                                    canCreateSearchAI && !isLoadingData && briefList.length && !searchAIList.length
                                ) && <SearchAiPlaceholder hasBriefData={Boolean(briefList.length)} />}

                                {/* Has Data */}
                                <Box
                                    sx={{
                                        pr: 1,
                                        maxHeight: 228,
                                        overflowY: "scroll",
                                        alignItems: "center",
                                    }}
                                >
                                    {canCreateSearchAI &&
                                        !isLoadingData &&
                                        searchAIFilteredList.length > 0 &&
                                        searchAIFilteredList.map((searchAI, i) => (
                                            <Box
                                                key={searchAI.id}
                                                sx={{
                                                    display: searchAI.shouldHide ? "none" : "flex",
                                                    alignItems: "center",
                                                    borderBottom:
                                                        i !== searchAIFilteredList.length - 1
                                                            ? `1px solid #EAEAEA`
                                                            : "",
                                                }}
                                            >
                                                <ExpandableCardRow
                                                    type="searchAi"
                                                    data={searchAI}
                                                    onClickRelatedButton={handleHighlightRelatedWork}
                                                >
                                                    <>
                                                        <Chip
                                                            sx={{
                                                                marginRight: theme.spacing(0.5),
                                                                backgroundColor: theme.palette.neutrals.light,
                                                                padding: theme.spacing(0.5, 1.2),
                                                                backdropFilter: "blur(10px)",
                                                                boxShadow:
                                                                    "0px 1px 4px rgba(0, 0, 0, 0.03), 0px 5px 5px rgba(0, 0, 0, 0.05), 0px 2px 2px rgba(0, 0, 0, 0.03)",
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.neutrals.light,
                                                                },
                                                                "&:active": {
                                                                    boxShadow:
                                                                        "0px 1px 4px rgba(0, 0, 0, 0.03), 0px 5px 5px rgba(0, 0, 0, 0.05), 0px 2px 2px rgba(0, 0, 0, 0.03)",
                                                                },
                                                                "&.Mui-disabled": {
                                                                    cursor: "none",
                                                                    opacity: 1,
                                                                },
                                                                "& .MuiChip-label": {
                                                                    fontWeight: 600,
                                                                    padding: 0,
                                                                    marginRight: theme.spacing(0.5),
                                                                    "& + div": {
                                                                        minWidth: 16,
                                                                        height: 16,
                                                                    },
                                                                },
                                                            }}
                                                            size="small"
                                                            variant="filled"
                                                            label="Company"
                                                            onDelete={(ev) =>
                                                                handleClickSearchAICount(
                                                                    ev,
                                                                    searchAI,
                                                                    SnackBarEntityTypes.company
                                                                )
                                                            }
                                                            deleteIcon={
                                                                <ButtonWithCounter
                                                                    count={searchAI.meta.companies_count}
                                                                    isDisabled={!searchAI.meta.companies_count}
                                                                    size={20}
                                                                    callback={(ev) => {
                                                                        handleClickSearchAICount(
                                                                            ev,
                                                                            searchAI,
                                                                            SnackBarEntityTypes.company
                                                                        );
                                                                    }}
                                                                    colors={["hovered", "info"]}
                                                                />
                                                            }
                                                            onClick={() => {
                                                                dispatch(clearChart());
                                                                navigate(
                                                                    `${urlPaths.Talent}/${companyId}?projectId=${searchAI.meta.projectId}&insightId=${searchAI.meta.relatedInsightId}&briefId=${searchAI.meta.relatedBriefId}&searchAiId=${searchAI.meta.pathId}`,
                                                                    {
                                                                        state: {
                                                                            from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                        },
                                                                    }
                                                                );
                                                            }}
                                                        />
                                                        <Chip
                                                            sx={{
                                                                marginRight: theme.spacing(0.5),
                                                                backgroundColor: theme.palette.neutrals.light,
                                                                padding: theme.spacing(0.5, 1.2),
                                                                backdropFilter: "blur(10px)",
                                                                boxShadow:
                                                                    "0px 1px 4px rgba(0, 0, 0, 0.03), 0px 5px 5px rgba(0, 0, 0, 0.05), 0px 2px 2px rgba(0, 0, 0, 0.03)",
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.neutrals.light,
                                                                },
                                                                "&:active": {
                                                                    boxShadow:
                                                                        "0px 1px 4px rgba(0, 0, 0, 0.03), 0px 5px 5px rgba(0, 0, 0, 0.05), 0px 2px 2px rgba(0, 0, 0, 0.03)",
                                                                },
                                                                "&.Mui-disabled": {
                                                                    cursor: "none",
                                                                    opacity: 1,
                                                                },
                                                                "& .MuiChip-label": {
                                                                    fontWeight: 600,
                                                                    padding: 0,
                                                                    marginRight: theme.spacing(0.5),
                                                                    "& + div": {
                                                                        minWidth: 16,
                                                                        height: 16,
                                                                    },
                                                                },
                                                            }}
                                                            size="small"
                                                            variant="filled"
                                                            label="People"
                                                            disabled={
                                                                isEmpty(searchAI.meta.relatedBrief) ||
                                                                !searchAI.meta.relatedCompanyListId
                                                            }
                                                            onDelete={(ev) =>
                                                                handleClickSearchAICount(
                                                                    ev,
                                                                    searchAI,
                                                                    SnackBarEntityTypes.company
                                                                )
                                                            }
                                                            deleteIcon={
                                                                <ButtonWithCounter
                                                                    count={searchAI.meta.people_count}
                                                                    isDisabled={!searchAI.meta.people_count}
                                                                    size={20}
                                                                    callback={(ev) => {
                                                                        handleClickSearchAICount(
                                                                            ev,
                                                                            searchAI,
                                                                            SnackBarEntityTypes.people
                                                                        );
                                                                    }}
                                                                    colors={["hovered", "info"]}
                                                                />
                                                            }
                                                            onClick={() => {
                                                                dispatch(clearChart());
                                                                navigate(
                                                                    `${urlPaths.TalentPeople}/${companyId}?projectId=${searchAI.meta.projectId}&insightId=${searchAI.meta.relatedInsightId}&briefId=${searchAI.meta.relatedBriefId}&searchAiId=${searchAI.meta.pathId}`,
                                                                    {
                                                                        state: {
                                                                            from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                        },
                                                                    }
                                                                );
                                                            }}
                                                        />
                                                        <Chip
                                                            sx={{
                                                                marginRight: theme.spacing(0.5),
                                                                backgroundColor: theme.palette.neutrals.light,
                                                                padding: theme.spacing(0.5, 1.2),
                                                                backdropFilter: "blur(10px)",
                                                                boxShadow:
                                                                    "0px 1px 4px rgba(0, 0, 0, 0.03), 0px 5px 5px rgba(0, 0, 0, 0.05), 0px 2px 2px rgba(0, 0, 0, 0.03)",
                                                                "&:hover": {
                                                                    backgroundColor: theme.palette.neutrals.light,
                                                                },
                                                                "&:active": {
                                                                    boxShadow:
                                                                        "0px 1px 4px rgba(0, 0, 0, 0.03), 0px 5px 5px rgba(0, 0, 0, 0.05), 0px 2px 2px rgba(0, 0, 0, 0.03)",
                                                                },
                                                                "&.Mui-disabled": {
                                                                    cursor: "none",
                                                                    opacity: 1,
                                                                },
                                                                "& .MuiChip-label": {
                                                                    fontWeight: 600,
                                                                    padding: 0,
                                                                    marginRight: theme.spacing(0.5),
                                                                    "& + div": {
                                                                        minWidth: 16,
                                                                        height: 16,
                                                                    },
                                                                },
                                                            }}
                                                            size="small"
                                                            variant="filled"
                                                            label="Shortlist"
                                                            disabled={
                                                                isEmpty(searchAI.meta.relatedBrief) ||
                                                                !searchAI.meta.relatedCompanyListId ||
                                                                !searchAI.meta.relatedLongListId
                                                            }
                                                            onDelete={(ev) =>
                                                                handleClickSearchAICount(
                                                                    ev,
                                                                    searchAI,
                                                                    SnackBarEntityTypes.company
                                                                )
                                                            }
                                                            deleteIcon={
                                                                <ButtonWithCounter
                                                                    count={searchAI.meta.shortlist_count}
                                                                    isDisabled={!searchAI.meta.shortlist_count}
                                                                    size={20}
                                                                    callback={(ev) => {
                                                                        handleClickSearchAICount(
                                                                            ev,
                                                                            searchAI,
                                                                            SnackBarEntityTypes.shortlist
                                                                        );
                                                                    }}
                                                                    colors={["hovered", "info"]}
                                                                />
                                                            }
                                                            onClick={() => {
                                                                dispatch(clearChart());
                                                                navigate(
                                                                    `${urlPaths.TalentRefinement}/${companyId}?projectId=${searchAI.meta.projectId}&insightId=${searchAI.meta.relatedInsightId}&briefId=${searchAI.meta.relatedBriefId}&searchAiId=${searchAI.meta.pathId}`,
                                                                    {
                                                                        state: {
                                                                            from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                                                        },
                                                                    }
                                                                );
                                                            }}
                                                        />
                                                    </>
                                                </ExpandableCardRow>

                                                <Box sx={{ ml: 2.5 }}>
                                                    <Button
                                                        variant="contained"
                                                        sx={{
                                                            minWidth: "max-content",
                                                            fontSize: 13,
                                                            height: 32,
                                                            mr: 2,
                                                            py: 0,
                                                            px: 2,
                                                            "& .MuiButton-startIcon > *:nth-of-type(1)": {
                                                                ml: 0,
                                                            },
                                                        }}
                                                        disabled={!searchAI.meta.shortlist_count}
                                                        onClick={() => handleCRMExport(searchAI.id)}
                                                        startIcon={
                                                            <CustomIcon
                                                                icon={icons.share}
                                                                sx={{ fontSize: "16px !important" }}
                                                            />
                                                        }
                                                    >
                                                        Export to CRM
                                                    </Button>
                                                </Box>
                                            </Box>
                                        ))}
                                </Box>

                                {openCRMExport && (
                                    <CRMExport
                                        open={openCRMExport}
                                        onClose={() => setOpenCRMExport(false)}
                                        searchAiId={selectedSearchAiId}
                                    />
                                )}

                                {/* No Results from filter */}
                                {!isLoadingData && !searchAIFilteredList.length && searchAIFilterValue && (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            height: 170,
                                            backgroundColor: "background.grey",
                                            margin: "auto",
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ margin: "auto" }}>
                                            <FormattedMessage id="dashboard.filters.noResults" />
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        </DashboardGroup>
                    )}

                    {/* Creation Modal */}
                    <CreateProjectSectionModal
                        modalFor={showModalFor}
                        isNameInvalid={isNameInvalid}
                        textInput={newSectioName}
                        textInputPlaceholder=""
                        handleTextInputChange={handleUpdateSectionName}
                        handleCreateSection={handleCreateProjectSection}
                        handleCloseModal={handleCloseProjectSectionCreateModal}
                    />

                    {/* SnackBar SearchAI */}
                    <SnackBar
                        maxWidth={wrapperRef.current?.getBoundingClientRect().width || 400}
                        shouldOpen={shouldOpenSnackBar}
                        anchorElement={popOverAnchorEl}
                        title={<strong>{capitalize(selectedEntity.type)}</strong>}
                        handleInfoClose={(ev) => {
                            handleCloseSnackBar(ev);
                            setSelectedEntity(entityInitialState);
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                        {...(wrapperRef.current
                            ? {
                                  anchorPosition: {
                                      top: wrapperRef.current?.getBoundingClientRect().bottom + 5,
                                      left:
                                          wrapperRef.current?.getBoundingClientRect().left +
                                          wrapperRef.current?.getBoundingClientRect().width / 2,
                                  },
                              }
                            : {})}
                        content={
                            <>
                                <RowHead
                                    title={
                                        <FormattedMessage
                                            id={`project.dashboard.popup.header.${
                                                selectedEntity.type === SnackBarEntityTypes.company
                                                    ? "company"
                                                    : "people"
                                            }.subterm`}
                                        />
                                    }
                                />
                                <Box sx={{ px: 2, maxHeight: 300, overflowY: "scroll" }}>
                                    {selectedEntity.type && !Boolean(selectedEntity.data.length) && (
                                        <>
                                            <Skeleton
                                                variant="rectangular"
                                                animation="wave"
                                                height={28}
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Skeleton
                                                variant="rectangular"
                                                animation="wave"
                                                height={28}
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Skeleton
                                                variant="rectangular"
                                                animation="wave"
                                                height={28}
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Skeleton
                                                variant="rectangular"
                                                animation="wave"
                                                height={28}
                                                sx={{ mb: 0.5 }}
                                            />
                                            <Skeleton variant="rectangular" animation="wave" height={28} />
                                        </>
                                    )}

                                    {selectedEntity.type === SnackBarEntityTypes.company &&
                                        (selectedEntity.data as TConvertedCompaniesList[])?.map((c) => (
                                            <Row
                                                key={c.id}
                                                term={c.name ?? ""}
                                                value={c.currentCapitalStructure ?? ""}
                                            />
                                        ))}

                                    {selectedEntity.type === SnackBarEntityTypes.people &&
                                        (selectedEntity.data as TConvertedPeopleList[])?.map((p) => (
                                            <Row
                                                key={p.id}
                                                term={p.name}
                                                subTerm={p.jobTitle}
                                                value={p.company ?? ""}
                                            />
                                        ))}

                                    {selectedEntity.type === SnackBarEntityTypes.shortlist &&
                                        (selectedEntity.data as TConvertedPeopleList[])?.map((p) => (
                                            <Row
                                                key={p.id}
                                                term={p.name}
                                                subTerm={p.jobTitle}
                                                value={p.company ?? ""}
                                            />
                                        ))}
                                </Box>
                            </>
                        }
                    />
                </Box>
            </Body>

            {/* CompanyInfo Modal */}
            <Modal open={isModalOpen}>
                <CompanyInfoCard onCloseModal={handleCloseModal} data={companyDataForSummaryCard} />
            </Modal>

            {/* Complete Insight Modal */}
            <Modal open={Boolean(showInfoForInsight.id)} onClose={() => setShowInfoForInsight(showInfoInsightInitial)}>
                <Box sx={{ width: "100%", maxWidth: 500, p: 2, borderRadius: 0.8, backgroundColor: "background.blue" }}>
                    <Box display="flex" alignItems="center">
                        <InfoIcon />
                        <Typography sx={{ fontSize: 18, ml: 1 }}>Incomplete LD Dashboard</Typography>
                    </Box>
                    <Box pt={1.2} pb={1}>
                        <Typography>
                            {showInfoForInsight.clickedOn === "Path"
                                ? "Complete your LD Dashboard to progress to Path to Improvement and view your Leadership Bridge."
                                : "Complete your LD Dashboard to Create Brief."}
                        </Typography>
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                        <Button onClick={() => setShowInfoForInsight(showInfoInsightInitial)}>Dismiss</Button>
                        <NavButton
                            onClick={() =>
                                navigate(getInsightMissingPortion(showInfoForInsight.id)?.url, {
                                    state: {
                                        from: `${urlPaths.ProjectDashboard}/${projectId}`,
                                    },
                                })
                            }
                        >{`${getInsightMissingPortion(showInfoForInsight.id)?.label}`}</NavButton>
                    </Box>
                </Box>
            </Modal>
        </ErrorBoundary>
    );
};

export default ProjectDashboard;
